import { useStore } from 'ra-core';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useAdvertiserData } from './useAdvertiserData';
import { Loading } from '@/components/admin';
import { PageTransition } from '@/components/admin/PageTransition';

// This component's only job is to manage the active advertiser context.
export const AdvertiserLayout = () => {
  const { advertiserId: rawAdvertiserId } = useParams<{ advertiserId: string }>();
  // Treat the literal string "undefined" as missing — NavigateAdvertiserIdPrefix
  // can produce /advertiser/undefined/... when localStorage is cleared.
  const advertiserId = rawAdvertiserId === 'undefined' ? undefined : rawAdvertiserId;
  const [storedAdvertiserId, setAdvertiserIdInStore] = useStore<
    string | undefined
  >('advertiser_id');
  const queryClient = useQueryClient();
  const { advertisers, isLoading, error } = useAdvertiserData();

  useEffect(() => {
    if (!advertiserId) return;
    setAdvertiserIdInStore(advertiserId);
    // Query keys here don't carry advertiser_id, so cached data collides
    // across advertisers. Invalidate (don't remove) so active observers
    // refetch with the new advertiser while their cached data stays visible
    // during the refetch — avoids loading-state flashes in the sidebar.
    if (storedAdvertiserId && storedAdvertiserId !== advertiserId) {
      queryClient.invalidateQueries();
    }
  }, [advertiserId, storedAdvertiserId, queryClient, setAdvertiserIdInStore]);

  if (isLoading) return <Loading />;
  if (error) return <div>{`Error loading data.`}</div>;
  if (!advertiserId) {
    if (advertisers.length === 0) {
      return <div>{`No advertisers assigned to this account.`}</div>;
    }
    // No valid advertiser ID in the URL — redirect to the first one from the
    // user's profile. This covers a cleared localStorage (where NavigateAdvertiserIdPrefix
    // would produce /advertiser/undefined/...) and any direct navigation to bare paths.
    return <Navigate to={`/advertiser/${advertisers[0].id}/campaigns`} replace />;
  }

  // The page transition lives HERE (below this layout) rather than around the
  // whole tree, so AdvertiserLayout stays mounted across navigations — no
  // remount flash, no per-navigation data refetch. PageTransition reads the
  // route via `useOutlet()` and owns its own Suspense boundary.
  return <PageTransition />;
};
